import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, money } from "../../lib/api";
import { Panel } from "./fields";
import { GoogleAdsPanel } from "./GoogleAdsPanel";

const d = (s) => format(parseISO(s), "d MMM yyyy");
const Th = ({ children }) => <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;

const Resend = ({ r }) => {
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reservations/${r.ref}/email`);
      data.email_status.startsWith("sent") ? toast.success("Confirmation email sent") : toast.error(data.email_status);
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <span className={`text-xs ${r.email_status.startsWith("failed") ? "text-red-600" : "text-ink/60"}`}>{r.email_status || "Not sent"}</span>
      <button className="btn-ghost !px-2 !py-1 text-xs" onClick={send} disabled={busy} data-testid={`resend-email-${r.ref}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}Resend
      </button>
    </div>
  );
};

const Reservations = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[900px] text-sm" data-testid="admin-reservations-table">
      <thead><tr className="border-b border-ink/10"><Th>Ref</Th><Th>Guest</Th><Th>Stay</Th><Th>Room / rate</Th><Th>Total</Th><Th>Deposit</Th><Th>Email</Th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.ref} className="border-b border-ink/5" data-testid={`admin-res-${r.ref}`}>
            <Td className="font-mono">{r.ref}<div className="text-xs capitalize text-ink/50">{r.status}</div></Td>
            <Td>{r.guest.first_name} {r.guest.last_name}<div className="text-xs text-ink/50">{r.guest.email} · {r.guest.phone}</div></Td>
            <Td>{d(r.arrival)} → {d(r.departure)}</Td>
            <Td>{r.quote.room.name}<div className="text-xs text-ink/50">{r.quote.rate.name}</div></Td>
            <Td className="font-mono">{money(r.quote.total, r.quote.currency)}</Td>
            <Td>{r.payment ? <>{money(r.payment.amount, r.payment.currency)}<div className="text-xs text-ink/50">{r.payment.mode === "simulated" ? "Simulated (not paid)" : r.payment.receipt}</div></> : "None"}
              <div className="text-xs text-ink/60" data-testid={`admin-balance-${r.ref}`}>{r.balance > 0 ? `Balance ${money(r.balance, r.quote.currency)} due ${d(r.balance_due)}` : "Fully paid"}{r.reminder_sent && " · reminded"}</div></Td>
            <Td><Resend r={r} /></Td>
          </tr>
        ))}
        {!rows.length && <tr><Td className="text-ink/50">No reservations yet</Td></tr>}
      </tbody>
    </table>
  </div>
);

const Checkouts = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[700px] text-sm" data-testid="admin-checkouts-table">
      <thead><tr className="border-b border-ink/10"><Th>Started</Th><Th>Guest</Th><Th>Room / arrival</Th><Th>Amount</Th><Th>Status</Th></tr></thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.cid} className="border-b border-ink/5">
            <Td>{format(parseISO(c.created_at), "d MMM, HH:mm")}</Td>
            <Td>{c.guest}<div className="text-xs text-ink/50">{c.email} · {c.phone}</div></Td>
            <Td>{c.room}<div className="text-xs text-ink/50">{d(c.arrival)}</div></Td>
            <Td className="font-mono">{money(c.amount, c.currency)}<div className="font-sans text-xs text-ink/50">{c.kind === "balance" ? "Balance payment" : "Deposit"}</div></Td>
            <Td><span className="capitalize">{c.status.replace("_", " ")}</span>{c.mode === "simulated" && <span className="text-xs text-ink/50"> (demo)</span>}{c.error && <div className="text-xs text-red-600">{c.error}</div>}{c.staff_error && <div className="text-xs text-ink/60" data-testid={`checkout-staff-error-${c.cid}`}>Gateway said: {c.staff_error}</div>}</Td>
          </tr>
        ))}
        {!rows.length && <tr><Td className="text-ink/50">No unfinished payments</Td></tr>}
      </tbody>
    </table>
  </div>
);

const LABELS = { direct: "Direct link", button: "Book Now button", search: "Search box", embed: "Full engine embed", google: "Google free listing", google_ads: "Google paid ads" };

const Stats = () => {
  const [days, setDays] = useState(30);
  const { data, isError } = useQuery({ queryKey: ["admin-stats", days], queryFn: () => api.get("/admin/stats", { params: { days } }).then((r) => r.data) });
  return (
    <Panel title="Where bookings come from" hint="Visits are counted once per guest session."
      action={<select className="field-input !w-auto" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="stats-days-select">
        {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
      </select>}>
      {isError ? <p className="text-sm text-red-600" data-testid="stats-error">Could not load the stats. Please refresh.</p> : !data ? <Loader2 className="h-5 w-5 animate-spin text-brand" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="stats-grid">
          {data.sources.map((s) => (
            <div key={s.source} className="rounded-xl border border-ink/10 p-4" data-testid={`stats-${s.source}`}>
              <p className="field-label">{LABELS[s.source]}</p>
              <p className="mt-2 font-mono text-2xl" data-testid={`stats-${s.source}-bookings`}>{s.bookings}<span className="text-sm text-ink/50"> {s.bookings === 1 ? "booking" : "bookings"}</span></p>
              <p className="text-sm text-ink/70" data-testid={`stats-${s.source}-revenue`}>{data.currency ? money(s.revenue, data.currency) : s.revenue.toLocaleString()}</p>
              <p className="mt-1 text-xs text-ink/50" data-testid={`stats-${s.source}-visits`}>{s.visits} {s.visits === 1 ? "visit" : "visits"} · {s.conversion === null ? "no visits yet" : `${s.conversion}% converted`}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export const BookingsTab = () => {
  const { data } = useQuery({ queryKey: ["admin-bookings"], queryFn: () => api.get("/admin/bookings").then((r) => r.data), refetchInterval: 30000 });
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;
  return (
    <div className="space-y-6" data-testid="admin-bookings-tab">
      <Stats />
      <GoogleAdsPanel />
      <Panel title="Reservations" hint="Bookings confirmed in eRes from this site."><Reservations rows={data.reservations} /></Panel>
      <Panel title="Unfinished payments" hint="Rooms held in eRes waiting for the deposit, or payments that failed."><Checkouts rows={data.checkouts} /></Panel>
    </div>
  );
};
